import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  createCuratedProduct,
  deleteCuratedProduct,
  listCuratedProducts,
  publishCuratedProduct,
  unpublishCuratedProduct,
  updateCuratedProduct,
} from '../../api/products';

const { Text } = Typography;

const categoryOptions = [
  { value: 1, label: '手机数码' },
  { value: 2, label: '服饰鞋包' },
  { value: 3, label: '家用电器' },
  { value: 4, label: '美妆护肤' },
  { value: 5, label: '食品生鲜' },
  { value: 6, label: '母婴玩具' },
  { value: 7, label: '家居家装' },
  { value: 8, label: '运动户外' },
];

const platformOptions = [
  { value: 'taobao', label: '淘宝' },
  { value: 'jd', label: '京东' },
  { value: 'pdd', label: '拼多多' },
  { value: 'manual', label: '其它' },
];

const statusOptions = [
  { value: 'PUBLISHED', label: '已上架' },
  { value: 'DRAFT', label: '草稿/下架' },
];

const splitImages = (value) =>
  String(value || '')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const joinImages = (images) => (Array.isArray(images) ? images.join('\n') : '');

const statusTag = (status) =>
  status === 'PUBLISHED' ? <Tag color="green">已上架</Tag> : <Tag>草稿/下架</Tag>;

const platformTag = (platform) => {
  const colorMap = {
    taobao: 'orange',
    jd: 'red',
    pdd: 'green',
    manual: 'blue',
  };
  const label = platformOptions.find((item) => item.value === platform)?.label || platform || '-';
  return <Tag color={colorMap[platform] || 'default'}>{label}</Tag>;
};

const CuratedProducts = () => {
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    keyword: '',
    platform: undefined,
    status: undefined,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchProducts = async (nextPagination = pagination, nextFilters = filters) => {
    setLoading(true);
    try {
      const result = await listCuratedProducts({
        page: nextPagination.current,
        size: nextPagination.pageSize,
        keyword: nextFilters.keyword || undefined,
        platform: nextFilters.platform || undefined,
        status: nextFilters.status || undefined,
      });
      setProducts(result.list || []);
      setPagination((prev) => ({
        ...prev,
        current: result.page || nextPagination.current,
        pageSize: result.size || nextPagination.pageSize,
        total: result.total || 0,
      }));
    } catch (error) {
      console.error('Failed to fetch curated products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateDrawer = () => {
    setEditingProduct(null);
    form.resetFields();
    form.setFieldsValue({
      platform: 'taobao',
      categoryId: 1,
      categoryName: '手机数码',
      sales: 0,
      commissionRate: 0,
      commissionAmount: 0,
      couponAmount: 0,
      sortOrder: 0,
      published: true,
    });
    setDrawerOpen(true);
  };

  const openEditDrawer = (record) => {
    setEditingProduct(record);
    form.setFieldsValue({
      ...record,
      published: record.status === 'PUBLISHED',
      imagesText: joinImages(record.images),
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        images: splitImages(values.imagesText),
      };
      delete payload.imagesText;

      if (editingProduct) {
        await updateCuratedProduct(editingProduct.id, payload);
        message.success('商品已更新');
      } else {
        await createCuratedProduct(payload);
        message.success('商品已创建');
      }
      setDrawerOpen(false);
      await fetchProducts();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error('Failed to save curated product:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (record) => {
    try {
      if (record.status === 'PUBLISHED') {
        await unpublishCuratedProduct(record.id);
        message.success('商品已下架');
      } else {
        await publishCuratedProduct(record.id);
        message.success('商品已上架');
      }
      await fetchProducts();
    } catch (error) {
      console.error('Failed to toggle curated product:', error);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deleteCuratedProduct(record.id);
      message.success('商品已删除');
      await fetchProducts();
    } catch (error) {
      console.error('Failed to delete curated product:', error);
    }
  };

  const handleSearch = () => {
    const nextPagination = { ...pagination, current: 1 };
    setPagination(nextPagination);
    fetchProducts(nextPagination);
  };

  const handleCategoryChange = (value, option) => {
    form.setFieldsValue({
      categoryId: value,
      categoryName: option?.label || '',
    });
  };

  const columns = [
    {
      title: '商品',
      dataIndex: 'title',
      width: 360,
      render: (_, record) => (
        <Space align="start">
          {record.imageUrl ? (
            <Image
              src={record.imageUrl}
              alt={record.title}
              width={64}
              height={64}
              preview={false}
              style={{ objectFit: 'cover', borderRadius: 10, background: '#f5f5f5' }}
            />
          ) : null}
          <Space direction="vertical" size={4}>
            <Text strong>{record.title}</Text>
            <Text type="secondary">{record.shopName || '未填写店铺'}</Text>
            <Text type="secondary">{record.categoryName}</Text>
          </Space>
        </Space>
      ),
    },
    {
      title: '平台',
      dataIndex: 'platform',
      width: 100,
      render: platformTag,
    },
    {
      title: '价格',
      dataIndex: 'price',
      width: 110,
      render: (value) => `¥${Number(value || 0).toFixed(2)}`,
    },
    {
      title: '佣金',
      width: 120,
      render: (_, record) => `¥${Number(record.commissionAmount || 0).toFixed(2)}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: statusTag,
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 80,
    },
    {
      title: '购买链接',
      dataIndex: 'productUrl',
      width: 120,
      render: (value) =>
        value ? (
          <a href={value} target="_blank" rel="noreferrer">
            打开
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: '操作',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEditDrawer(record)}>
            编辑
          </Button>
          <Button size="small" icon={<UploadOutlined />} onClick={() => handleTogglePublish(record)}>
            {record.status === 'PUBLISHED' ? '下架' : '上架'}
          </Button>
          <Popconfirm
            title="删除商品"
            description="删除后不可恢复，确定继续吗？"
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ marginBottom: 4 }}>精选商品</h2>
            <Text type="secondary">录入真实商品素材，先支撑 APP 审核和媒体备案。</Text>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateDrawer}>
            录入商品
          </Button>
        </Space>

        <Card>
          <Space wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="搜索标题、店铺或分类"
              value={filters.keyword}
              onChange={(event) => setFilters((prev) => ({ ...prev, keyword: event.target.value }))}
              onPressEnter={handleSearch}
              style={{ width: 260 }}
            />
            <Select
              allowClear
              placeholder="平台"
              value={filters.platform}
              onChange={(value) => setFilters((prev) => ({ ...prev, platform: value }))}
              options={platformOptions}
              style={{ width: 140 }}
            />
            <Select
              allowClear
              placeholder="状态"
              value={filters.status}
              onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
              options={statusOptions}
              style={{ width: 140 }}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              查询
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetchProducts()}>
              刷新
            </Button>
          </Space>
        </Card>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={products}
          scroll={{ x: 1280 }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个商品`,
            onChange: (current, pageSize) => {
              const nextPagination = { ...pagination, current, pageSize };
              setPagination(nextPagination);
              fetchProducts(nextPagination);
            },
          }}
        />
      </Space>

      <Drawer
        title={editingProduct ? '编辑精选商品' : '录入精选商品'}
        width={760}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={handleSubmit}>
              保存
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="商品标题" rules={[{ required: true, message: '请输入商品标题' }]}>
            <Input placeholder="例：品牌型号 + 规格 + 活动信息" maxLength={120} showCount />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="platform" label="平台" rules={[{ required: true, message: '请选择平台' }]}>
                <Select options={platformOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shopName" label="店铺名称">
                <Input placeholder="例：官方旗舰店" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="categoryId" label="分类" rules={[{ required: true, message: '请选择分类' }]}>
                <Select options={categoryOptions} onChange={handleCategoryChange} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="categoryName" label="分类名称" rules={[{ required: true, message: '请输入分类名称' }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="price" label="当前价" rules={[{ required: true, message: '请输入当前价' }]}>
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="originalPrice" label="原价/参考价">
                <InputNumber min={0.01} precision={2} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sales" label="销量/热度">
                <InputNumber min={0} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="imageUrl" label="商品主图 URL" rules={[{ required: true, message: '请输入商品主图 URL' }]}>
            <Input placeholder="建议使用联盟后台、商家授权素材或稳定 CDN 图片" />
          </Form.Item>

          <Form.Item name="imagesText" label="更多图片 URL">
            <Input.TextArea rows={3} placeholder="每行一个图片 URL，可不填" />
          </Form.Item>

          <Form.Item name="productUrl" label="购买链接" rules={[{ required: true, message: '请输入购买链接' }]}>
            <Input placeholder="第三方平台商品链接或联盟后台生成的推广链接" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sourceProductId" label="外部商品 ID">
                <Input placeholder="可选，如淘宝 num_iid" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sourceMaterialId" label="外部物料 ID">
                <Input placeholder="可选" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="commissionRate" label="佣金比例">
                <InputNumber min={0} precision={2} addonAfter="%" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="commissionAmount" label="佣金金额">
                <InputNumber min={0} precision={2} addonBefore="¥" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="couponAmount" label="优惠金额">
                <InputNumber min={0} precision={2} addonBefore="¥" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="couponLink" label="优惠券链接">
            <Input placeholder="可选，只有确认真实可用再填写" />
          </Form.Item>

          <Form.Item name="description" label="推荐理由/商品描述">
            <Input.TextArea rows={3} maxLength={300} showCount />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sortOrder" label="排序权重">
                <InputNumber min={0} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="published" label="立即上架" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="note" label="内部备注">
            <Input.TextArea rows={2} placeholder="只给后台看，例如数据来源、素材授权说明" />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default CuratedProducts;
